import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import { DomainApiError } from '../lib/api-error.js';
import {
  connectMailbox,
  disconnectMailbox,
  getMailbox,
  MAILBOX_CARDS_QUERY_KEY,
  testMailbox,
  type ConnectMailboxInput,
  type MailboxPresetKey,
} from '../lib/mailbox-api.js';

/** Sdílený klíč: pás nad tabulí Úkoly čte tentýž stav schránky. */
export const MAILBOX_QUERY_KEY = ['mailbox', 'view'] as const;

const PRESETS: readonly MailboxPresetKey[] = ['gmail', 'seznam', 'outlook', 'custom'];

/** Strojové kódy z „Ověřit připojení" → klíč katalogu (neznámý = obecná chyba). */
// `host_blocked` = krok MRGS1 bod 3: zadany server miri do vnitrni site.
// `smtp_*` = krok MAIL1 bod 2: odchozi server (odesilani) je oddeleny vysledek.
const ERROR_CODES = [
  'login_failed',
  'timeout',
  'host_not_found',
  'unreachable',
  'tls_error',
  'host_blocked',
  'smtp_login_failed',
  'smtp_timeout',
  'smtp_unreachable',
  'smtp_tls_error',
  'smtp_not_set',
  'smtp_error',
] as const;
type ErrorCode = (typeof ERROR_CODES)[number] | 'imap_error';

export function mailboxErrorCode(code: string | null | undefined): ErrorCode {
  const plain = (code ?? '').replace(/^broker-inbox\/connection-failed:/, '');
  return (ERROR_CODES as readonly string[]).includes(plain) ? (plain as ErrorCode) : 'imap_error';
}

/**
 * Krok AS1 bod 1 — Nastavení → „Schránka" (per uživatel).
 *
 * Předvolby Gmail / Seznam / Outlook / vlastní server, „Ověřit připojení"
 * (LOGIN + EXAMINE, nic se neuloží) a „Připojit" — server uloží jen
 * schránku, ke které se opravdu přihlásil. Heslo aplikace se po odeslání
 * z formuláře maže; zpátky ze serveru nepřijde nikdy.
 *
 * ROZHODNUTÍ AS1-OAUTH: zatím jen IMAP s heslem aplikace — v REOS není
 * Google OAuth, který by šel použít bez nové registrace u Googlu.
 */
export function MailboxSettingsCard(): React.JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const view = useQuery({ queryKey: MAILBOX_QUERY_KEY, queryFn: getMailbox });
  const [preset, setPreset] = React.useState<MailboxPresetKey>('seznam');
  const [host, setHost] = React.useState('');
  const [port, setPort] = React.useState('993');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [share, setShare] = React.useState(false);
  /**
   * Krok MAIL1 bod 2 — odesílání z téže schránky. Zapnuté je VÝCHOZÍ: makléř,
   * který schránku připojuje, ji chce použít celou. Vypnutí je vědomá volba
   * („schránka ať jen čte").
   */
  const [sending, setSending] = React.useState(true);
  const [smtpHost, setSmtpHost] = React.useState('');
  const [smtpPort, setSmtpPort] = React.useState('587');
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [probes, setProbes] = React.useState<{
    readonly imap: string;
    readonly imapOk: boolean;
    readonly smtp: string | null;
    readonly smtpOk: boolean;
  } | null>(null);

  function input(): ConnectMailboxInput {
    return {
      preset,
      ...(preset === 'custom' ? { host: host.trim(), port: Number(port) } : {}),
      username: username.trim(),
      password,
      shareWithOwner: share,
      sending,
      // Předvolby znají SMTP samy (server); vyplňuje se jen u „vlastní".
      ...(sending && preset === 'custom' ? { smtpHost: smtpHost.trim(), smtpPort: Number(smtpPort) } : {}),
    };
  }

  function failure(code: string | null | undefined): string {
    return t(`webAdmin.mailbox.error.${mailboxErrorCode(code)}` as 'webAdmin.mailbox.error.imap_error');
  }

  /**
   * Krok MAIL1 bod 2 — výsledek je DVOJÍ (čtení a odesílání). Jedna věta
   * „nepovedlo se" by makléři zatajila, že čtení funguje a jen odesílání ne,
   * a on by zbytečně hledal chybu v hesle.
   */
  async function onTest(): Promise<void> {
    setBusy(true);
    setNote(null);
    setProbes(null);
    try {
      const result = await testMailbox(input());
      setProbes({
        imap: result.imap.ok ? t('webAdmin.mailbox.testImapOk') : failure(result.imap.code),
        imapOk: result.imap.ok,
        // Bez odchoziho serveru se SMTP netestuje — tehdy se neukazuje nic.
        smtp: sending ? (result.smtp.ok ? t('webAdmin.mailbox.testSmtpOk') : failure(result.smtp.code)) : null,
        smtpOk: result.smtp.ok,
      });
    } catch (e) {
      setNote({ ok: false, text: e instanceof DomainApiError ? e.message : failure(null) });
    } finally {
      setBusy(false);
    }
  }

  async function onConnect(): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      await connectMailbox(input());
      setPassword('');
      setNote({ ok: true, text: t('webAdmin.mailbox.connected') });
      await queryClient.invalidateQueries({ queryKey: MAILBOX_QUERY_KEY });
    } catch (e) {
      setNote({ ok: false, text: e instanceof DomainApiError && e.type !== undefined ? failure(e.type) : failure(null) });
    } finally {
      setBusy(false);
    }
  }

  async function onDisconnect(): Promise<void> {
    if (!window.confirm(t('webAdmin.mailbox.disconnectConfirm'))) return;
    setBusy(true);
    try {
      await disconnectMailbox();
      setNote({ ok: true, text: t('webAdmin.mailbox.disconnected') });
      await queryClient.invalidateQueries({ queryKey: MAILBOX_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: MAILBOX_CARDS_QUERY_KEY });
    } catch {
      setNote({ ok: false, text: t('webAdmin.mailbox.disconnectFailed') });
    } finally {
      setBusy(false);
    }
  }

  const mailbox = view.data?.connected === true ? view.data.mailbox : null;
  const canSubmit =
    !busy &&
    username.trim() !== '' &&
    password !== '' &&
    (preset !== 'custom' || host.trim() !== '') &&
    // Odesilani „vlastnim serverem" bez adresy SMTP nejde ani zkusit.
    (!sending || preset !== 'custom' || smtpHost.trim() !== '');

  return (
    <div id="schranka" className="rh-card" style={{ maxWidth: 640, padding: 16, marginBottom: 16 }} data-testid="nastaveni-mailbox-card" data-guide="mailbox">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.mailbox.title')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.mailbox.perex')}</p>

      {mailbox !== null ? (
        <div data-testid="mailbox-connected">
          <p className="mb-1 text-sm">
            <strong>{mailbox.address}</strong>{' '}
            <span className="rh-a2-badge" data-testid="mailbox-status">
              {t(`webAdmin.mailbox.status.${mailbox.status}` as 'webAdmin.mailbox.status.connected')}
            </span>
          </p>
          {mailbox.lastError !== null ? (
            <p className="mb-2 text-sm text-red-700" role="alert" data-testid="mailbox-last-error">
              {t('webAdmin.mailbox.lastError')}
            </p>
          ) : null}
          <p className="mb-2 text-xs text-gray-600">
            {mailbox.shareWithOwner ? t('webAdmin.mailbox.sharedOn') : t('webAdmin.mailbox.sharedOff')}
          </p>
          {/* Krok MAIL1 bod 2: makléř musí z obrazovky poznat, jestli klientské
              e-maily odejdou z JEHO adresy, nebo za kancelář. */}
          <p className="mb-2 text-xs text-gray-600" data-testid="mailbox-sending">
            {mailbox.smtpHost !== null
              ? t('webAdmin.mailbox.sendingOn', { host: mailbox.smtpHost })
              : t('webAdmin.mailbox.sendingOff')}
          </p>
          {mailbox.smtpLastError !== null ? (
            <p className="mb-2 text-sm text-red-700" role="alert" data-testid="mailbox-smtp-error">
              {t('webAdmin.mailbox.smtpLastError')}
            </p>
          ) : null}
          <button type="button" className="rh-a2-btn" disabled={busy} data-testid="mailbox-disconnect" onClick={() => void onDisconnect()}>
            {t('webAdmin.mailbox.disconnect')}
          </button>
          <p className="mt-2 text-xs text-gray-600">{t('webAdmin.mailbox.disconnectHint')}</p>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void onConnect();
          }}
          data-testid="mailbox-form"
        >
          <fieldset className="mb-2">
            <legend className="mb-1 text-sm font-medium">{t('webAdmin.mailbox.presetLabel')}</legend>
            <div className="rh-a2-seg" role="group">
              {PRESETS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={preset === key ? 'is-on' : ''}
                  aria-pressed={preset === key}
                  data-testid={`mailbox-preset-${key}`}
                  onClick={() => setPreset(key)}
                >
                  {t(`webAdmin.mailbox.preset.${key}` as 'webAdmin.mailbox.preset.gmail')}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-600" data-testid="mailbox-preset-hint">
              {t(`webAdmin.mailbox.presetHint.${preset}` as 'webAdmin.mailbox.presetHint.gmail')}
            </p>
          </fieldset>
          {preset === 'custom' ? (
            <div className="mb-2 flex gap-2">
              <label className="block flex-1 text-sm">
                {t('webAdmin.mailbox.hostLabel')}
                <input className="block w-full rounded border px-2 py-1" value={host} onChange={(e) => setHost(e.target.value)} data-testid="mailbox-host" />
              </label>
              <label className="block w-24 text-sm">
                {t('webAdmin.mailbox.portLabel')}
                <input className="block w-full rounded border px-2 py-1" inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value)} data-testid="mailbox-port" />
              </label>
            </div>
          ) : null}
          <label className="mb-2 block text-sm">
            {t('webAdmin.mailbox.usernameLabel')}
            <input className="block w-full rounded border px-2 py-1" type="email" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="mailbox-username" />
          </label>
          <label className="mb-2 block text-sm">
            {t('webAdmin.mailbox.passwordLabel')}
            <input className="block w-full rounded border px-2 py-1" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="mailbox-password" />
          </label>
          <label className="mb-1 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sending} onChange={(e) => setSending(e.target.checked)} data-testid="mailbox-sending-toggle" />
            {t('webAdmin.mailbox.sendingLabel')}
          </label>
          <p className="mb-2 text-xs text-gray-600" data-testid="mailbox-sending-hint">
            {t('webAdmin.mailbox.sendingHint')}
          </p>
          {sending && preset === 'custom' ? (
            <div className="mb-2 flex gap-2">
              <label className="block flex-1 text-sm">
                {t('webAdmin.mailbox.smtpHostLabel')}
                <input className="block w-full rounded border px-2 py-1" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} data-testid="mailbox-smtp-host" />
              </label>
              <label className="block w-24 text-sm">
                {t('webAdmin.mailbox.smtpPortLabel')}
                <input className="block w-full rounded border px-2 py-1" inputMode="numeric" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} data-testid="mailbox-smtp-port" />
              </label>
            </div>
          ) : null}
          <label className="mb-3 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} data-testid="mailbox-share" />
            {t('webAdmin.mailbox.shareLabel')}
          </label>
          <div className="flex gap-2">
            <button type="button" className="rh-a2-btn" disabled={!canSubmit} data-testid="mailbox-test" onClick={() => void onTest()}>
              {t('webAdmin.mailbox.test')}
            </button>
            <button type="submit" className="rh-a2-btn rh-a2-btn--primary" disabled={!canSubmit} data-testid="mailbox-connect">
              {t('webAdmin.mailbox.connect')}
            </button>
          </div>
        </form>
      )}
      {probes !== null ? (
        <div className="mt-2 text-sm" data-testid="mailbox-probes">
          <p className={probes.imapOk ? 'text-green-700' : 'text-red-700'} role={probes.imapOk ? 'status' : 'alert'} data-testid="mailbox-probe-imap">
            {probes.imap}
          </p>
          {probes.smtp !== null ? (
            <p className={probes.smtpOk ? 'text-green-700' : 'text-red-700'} role={probes.smtpOk ? 'status' : 'alert'} data-testid="mailbox-probe-smtp">
              {probes.smtp}
            </p>
          ) : null}
        </div>
      ) : null}
      {note !== null ? (
        <p className={`mt-2 text-sm ${note.ok ? 'text-green-700' : 'text-red-700'}`} role={note.ok ? 'status' : 'alert'} data-testid="mailbox-note">
          {note.text}
        </p>
      ) : null}
      <p className="mt-3 text-xs text-gray-600">{t('webAdmin.mailbox.promise')}</p>
    </div>
  );
}
